// APFormShell — full-page create/edit form wired to a useAPForm instance.
// The DEFAULT form surface for v4 (dev decision, post-review round): route-
// driven pages with a constrained content column and a STICKY header that
// carries the back-chevron + title on the start side and cancel/save on the
// end side (no footer bar — the actions ride the header so they stay reachable
// as long forms scroll). APFormDialog remains the opt-in alternative for
// genuinely small forms.
import { AltArrowLeftLinear } from '@assessprep/icons/solar';
import { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';
import type { UseAPFormReturn } from '@/lib/form';

export interface APFormShellProps<TVariables extends FieldValues = FieldValues> {
  baseForm: UseAPFormReturn<TVariables>;
  /** Already-translated page title. */
  title: ReactNode;
  /** Already-translated supporting copy under the title. */
  description?: ReactNode;
  /** Leave the page — back button, cancel, and (unless `onSaved` is given)
   *  save-success all land here (typically `navigate` back to the list route). */
  onBack: () => void;
  /** Save-success override — receives the mutation result; defaults to `onBack`. */
  onSaved?: (result: unknown) => void;
  /** Field components from the APFormField kit (rendered inside the RHF provider). */
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  /** Drop the single card body so children (self-carded sections via
   *  APFormSection) sit directly on the canvas. Default: one floating card. */
  sectioned?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function APFormShell<TVariables extends FieldValues = FieldValues>({
  baseForm,
  title,
  description,
  onBack,
  onSaved,
  children,
  submitLabel,
  cancelLabel,
  sectioned = false,
  className,
  'data-testid': testId = 'form-page',
}: APFormShellProps<TVariables>) {
  const { t } = useTranslation();
  const { form, submit, cancel, formLoading, showSkeleton } = baseForm;

  const handleSubmit = async () => {
    const result = await submit();
    // submit() resolves undefined when validation or the mutation failed —
    // stay on the page so the user sees the field errors / toast.
    if (result !== undefined) {
      if (onSaved) {
        onSaved(result);
      } else {
        onBack();
      }
    }
  };

  const handleCancel = () => {
    cancel();
    onBack();
  };

  return (
    <div
      // pb-24: scroll-past room below the last card (window is the scroll container).
      className={cn('mx-auto flex w-full max-w-2xl flex-col gap-6 pb-24', className)}
      data-testid={testId}
    >
      {/* Sticky action header: pins below the (desktop: absent) top chrome via
          top-0 on the window scroll container — the v4 shell has no fixed top
          navbar, only a left sidebar, so the bar reads flush with the canvas.
          bg-ap-canvas keeps it opaque + blended so cards scroll cleanly under. */}
      <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-ap-canvas py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('CommonText.back')}
            onClick={handleCancel}
            data-testid={`${testId}-back`}
          >
            <AltArrowLeftLinear aria-hidden="true" className="rtl:rotate-180" />
          </Button>
          <div className="min-w-0 space-y-1">
            <h1 className="truncate text-2xl leading-none font-semibold tracking-tight">{title}</h1>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
        {/* Actions hidden during initial hydration (edit) — the back-chevron
            still leaves the page; save has nothing to submit until loaded. */}
        {!showSkeleton && (
          // pe-6 aligns the action cluster's end with the cards' INNER content
          // edge (Card px-6), not their outer border (Shikhar, 2026-07-15).
          <div className="flex shrink-0 items-center gap-2 pe-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={formLoading}
              data-testid={`${testId}-cancel`}
            >
              {cancelLabel ?? t('CommonText.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={formLoading}
              data-testid={`${testId}-save`}
            >
              {formLoading && <Spinner aria-hidden="true" />}
              {submitLabel ?? t('CommonText.save')}
            </Button>
          </div>
        )}
      </div>
      {showSkeleton ? (
        <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
          <div className="grid gap-3" data-testid={`${testId}-skeleton`}>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        </div>
      ) : (
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit();
            }}
          >
            {/* Sectioned forms bring their own APFormSection cards (deliver look);
                the default keeps fields on one floating card (LU3, dev-approved).
                APFormDialog is untouched either way. */}
            {sectioned ? (
              <div className="grid gap-4">{children}</div>
            ) : (
              <div className="grid gap-4 rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
                {children}
              </div>
            )}
            {/* Save moved to the sticky header (outside <form>), so this
                sr-only submit keeps native Enter-key submission working. */}
            <Button type="submit" disabled={formLoading} tabIndex={-1} className="sr-only">
              {submitLabel ?? t('CommonText.save')}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
